import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { socket, requestData } from "../../socket";
import { useAuth } from "../../AuthContext";
import AppContext from "../../app-context";
import UsersListTab from "./users/users-list-tab";
import UserInvitationsTab from "./users/user-invitations-tab";

const dict = {
    en: {
        title: "Users",
        subTitle: "add / edit users",
        homeURL: "/dashboard",
        addTitle: "Invite User",
        editTitle: "Edit User",

        colName: "Name",
        colUserRole: "User Role",
        colTimeZone: "Time Zone",
        colRegDate: "Registration Date",

        na: "N/A",

        roleAdmin: "Administrator",
        roleMember: "Member",
        roleOwner: "Owner",
        roleUnknown: "Unknown",

        tabMain: "Main",
        fn: "First Name",
        ln: "Last Name",
        email: "Email",
        userRole: "User Role",
        phone: "Phone",
        country: "Country",
        timeZone: "Time Zone",

        inviteLine1:
            "Please be advised: the invited user needs to follow the link received in the email and complete a simplified registration process.",
        inviteLine2:
            "The user does not need to visit the site, go through the full registration, or create a new company.",

        edit: "Edit",
        delete: "Delete",
        suspend: "Suspend user",
        suspend: "Suspend user",
        suspend: "Suspend user",

        delAlert: "Deleting a user cannot be undone.",
        delText: "To confirm deletion, enter the name of the user in the next input field",
        delTitle: 'Delete user: "{name}"',

        toastEntity: "User",
        createdOk: "created successfully",
        updatedOk: "updated successfully",
        deletedOk: "deleted successfully",
        statusFilterLabel: "Status",
        filterAll: "All users",
        filterActive: "Active users",
        filterInactive: "Suspended users",
        activateAction: "Activate",
        confirmSuspend: "Suspend this user? They will lose access until re-activated.",
        confirmActivate: "Activate this user and restore access?",
        suspendSuccess: "User suspended.",
        activateSuccess: "User activated.",
        tabUsers: "Users",
        tabInvites: "Invitations",
        invitesTitle: "Invitations",
        invitesSubtitle: "Pending and sent invitations",
        invitesEmpty: "No invitations found",
        inviteColEmail: "Email",
        inviteColRole: "Role",
        inviteColStatus: "Status",
        inviteColSent: "Sent",
        inviteColExpires: "Expires",
        inviteColInvitedBy: "Invited By",
        inviteStatusPending: "Pending",
        inviteStatusAccepted: "Accepted",
        inviteStatusExpired: "Expired",
        inviteStatusRevoked: "Revoked",
        inviteResend: "Resend",
        inviteCancel: "Cancel",
        inviteResendSuccess: "Invitation resent.",
        inviteCancelSuccess: "Invitation revoked.",
        inviteStatusSuccess: "Invitation updated.",
        inviteActionError: "Action failed. Try again.",
        invitesLoading: "Loading invitations…",
        inviteActions: "Actions",
        searchPlaceholder: "Search…",
        inviteAddTitle: "Send Invitation",
        inviteEmailLabel: "Invitee email",
        inviteFirstNameLabel: "First name",
        inviteLastNameLabel: "Last name",
        inviteRoleLabel: "Assign role",
        inviteCreatedOk: "Invitation sent successfully.",
        tzSelect: "Select time zone",
        tzLoading: "Loading time zones…",
        tzEmpty: "No time zones",
        memberManageRestricted: "Members cannot manage users.",
        memberInviteRestricted: "Members cannot send invitations.",
    },

    ru: {
        title: "Пользователи",
        subTitle: "добавление / редактирование пользователей",
        homeURL: "/dashboard",
        addTitle: "Пригласить пользователя",
        editTitle: "Редактировать пользователя",

        colName: "Имя",
        colUserRole: "Роль",
        colTimeZone: "Часовой пояс",
        colRegDate: "Дата регистрации",

        na: "Н/Д",

        roleAdmin: "Администратор",
        roleMember: "Участник",
        roleOwner: "Владелец",
        roleUnknown: "Неизвестно",

        tabMain: "Основное",
        fn: "Имя",
        ln: "Фамилия",
        email: "Email",
        userRole: "Роль пользователя",
        phone: "Телефон",
        country: "Страна",
        timeZone: "Часовой пояс",

        inviteLine1:
            "Обратите внимание: приглашённому пользователю необходимо перейти по ссылке из письма и завершить упрощённую регистрацию.",
        inviteLine2:
            "Пользователю не нужно заходить на сайт, проходить полную регистрацию или создавать новую компанию.",

        edit: "Редактировать",
        delete: "Удалить",
        suspend: "Заморозить пользователя",

        delAlert: "Удаление пользователя необратимо.",
        delText: "Чтобы подтвердить удаление, введите имя пользователя в следующее поле ввода",
        delTitle: 'Удалить пользователя: «{name}»',

        toastEntity: "Пользователь",
        createdOk: "успешно создан",
        updatedOk: "успешно обновлён",
        deletedOk: "успешно удалён",
        statusFilterLabel: "Статус",
        filterAll: "Все пользователи",
        filterActive: "Активные",
        filterInactive: "Неактивные",
        activateAction: "Активировать",
        confirmSuspend: "Приостановить доступ пользователя? Доступ будет отключён до повторной активации.",
        confirmActivate: "Активировать пользователя и вернуть доступ?",
        suspendSuccess: "Пользователь заморожен.",
        activateSuccess: "Пользователь активирован.",
        tabUsers: "Пользователи",
        tabInvites: "Приглашения",
        invitesTitle: "Приглашения",
        invitesSubtitle: "Отправленные приглашения в рабочее пространство",
        invitesEmpty: "Приглашений нет",
        inviteColEmail: "Email",
        inviteColRole: "Роль",
        inviteColStatus: "Статус",
        inviteColSent: "Отправлено",
        inviteColExpires: "Истекает",
        inviteColInvitedBy: "Кто пригласил",
        inviteStatusPending: "Ожидает",
        inviteStatusAccepted: "Принято",
        inviteStatusExpired: "Истекло",
        inviteStatusRevoked: "Отозвано",
        inviteResend: "Отправить снова",
        inviteCancel: "Отменить",
        inviteResendSuccess: "Приглашение повторно отправлено.",
        inviteCancelSuccess: "Приглашение отозвано.",
        inviteStatusSuccess: "Приглашение обновлено.",
        inviteActionError: "Не удалось выполнить действие. Попробуйте снова.",
        invitesLoading: "Загрузка приглашений…",
        inviteActions: "Действия",
        searchPlaceholder: "Поиск…",
        inviteAddTitle: "Отправить приглашение",
        inviteEmailLabel: "Email приглашённого",
        inviteFirstNameLabel: "Имя",
        inviteLastNameLabel: "Фамилия",
        inviteRoleLabel: "Назначить роль",
        inviteCreatedOk: "Приглашение отправлено.",
        tzSelect: "Выберите часовой пояс",
        tzLoading: "Загрузка часовых поясов…",
        tzEmpty: "Нет данных о часовых поясах",
        memberManageRestricted: "У участников нет прав управлять пользователями.",
        memberInviteRestricted: "У участников нет прав отправлять приглашения.",
    },

    ua: {
        title: "Користувачі",
        subTitle: "додавання / редагування користувачів",
        homeURL: "/dashboard",
        addTitle: "Запросити користувача",
        editTitle: "Редагувати користувача",

        colName: "Ім’я",
        colUserRole: "Роль",
        colTimeZone: "Часовий пояс",
        colRegDate: "Дата реєстрації",

        na: "Н/Д",

        roleAdmin: "Адміністратор",
        roleMember: "Учасник",
        roleOwner: "Власник",
        roleUnknown: "Невідомо",

        tabMain: "Основне",
        fn: "Ім’я",
        ln: "Прізвище",
        email: "Email",
        userRole: "Роль користувача",
        phone: "Телефон",
        country: "Країна",
        timeZone: "Часовий пояс",

        inviteLine1:
            "Зверніть увагу: запрошений користувач має перейти за посиланням з листа та завершити спрощену реєстрацію.",
        inviteLine2:
            "Користувачу не потрібно заходити на сайт, проходити повну реєстрацію чи створювати нову компанію.",

        edit: "Редагувати",
        delete: "Видалити",
        suspend: "Призупинити користувача",

        delAlert: "Видалення користувача неможливо скасувати.",
        delText: "Щоб підтвердити видалення, введіть ім’я користувача у наступне поле вводу",
        delTitle: 'Видалити користувача: «{name}»',

        toastEntity: "Користувач",
        createdOk: "успішно створено",
        updatedOk: "успішно оновлено",
        deletedOk: "успішно видалено",
        statusFilterLabel: "Статус",
        filterAll: "Усі користувачі",
        filterActive: "Активні",
        filterInactive: "Неактивні",
        activateAction: "Активувати",
        confirmSuspend: "Призупинити доступ користувача? Доступ буде вимкнено до повторної активації.",
        confirmActivate: "Активувати користувача й повернути доступ?",
        suspendSuccess: "Користувача призупинено.",
        activateSuccess: "Користувача активовано.",
        tabUsers: "Користувачі",
        tabInvites: "Запрошення",
        invitesTitle: "Запрошення",
        invitesSubtitle: "Надіслані запрошення до робочого простору",
        invitesEmpty: "Запрошень немає",
        inviteColEmail: "Email",
        inviteColRole: "Роль",
        inviteColStatus: "Статус",
        inviteColSent: "Надіслано",
        inviteColExpires: "Дійсне до",
        inviteColInvitedBy: "Хто запросив",
        inviteStatusPending: "Очікує",
        inviteStatusAccepted: "Прийнято",
        inviteStatusExpired: "Строк минув",
        inviteStatusRevoked: "Скасовано",
        inviteResend: "Надіслати ще раз",
        inviteCancel: "Відмінити",
        inviteResendSuccess: "Запрошення повторно надіслано.",
        inviteCancelSuccess: "Запрошення скасовано.",
        inviteStatusSuccess: "Запрошення оновлено.",
        inviteActionError: "Не вдалося виконати дію. Спробуйте пізніше.",
        invitesLoading: "Завантаження запрошень…",
        inviteActions: "Дії",
        searchPlaceholder: "Пошук…",
        inviteAddTitle: "Надіслати запрошення",
        inviteEmailLabel: "Email запрошеного",
        inviteFirstNameLabel: "Ім’я",
        inviteLastNameLabel: "Прізвище",
        inviteRoleLabel: "Призначити роль",
        inviteCreatedOk: "Запрошення надіслано.",
        tzSelect: "Оберіть часовий пояс",
        tzLoading: "Завантаження часових поясів…",
        tzEmpty: "Немає часових поясів",
        memberManageRestricted: "У учасників немає прав керувати користувачами.",
        memberInviteRestricted: "У учасників немає прав надсилати запрошення.",
    },
};

const getInitialLang = () => (localStorage.getItem("lang") || "en").toLowerCase();

const resolveUserRole = (authUser = {}) => {
    const storedRole = typeof window !== "undefined" ? localStorage.getItem("user_role") : "";
    const candidates = [
        authUser?.user_role,
        authUser?.user_type,
        authUser?.role,
        authUser?.workspace_role,
        authUser?.company_role,
        authUser?.role_name,
        authUser?.access_role,
        authUser?.forward?.user_role,
        authUser?.forward?.role,
        storedRole,
    ];

    for (const value of candidates) {
        if (typeof value === "string" && value.trim()) {
            return value.trim().toLowerCase();
        }
    }

    if (authUser?.allow && typeof authUser.allow === "object") {
        const allowCandidates = [authUser.allow.user_role, authUser.allow.role];
        for (const value of allowCandidates) {
            if (typeof value === "string" && value.trim()) {
                return value.trim().toLowerCase();
            }
        }
    }

    return "";
};

const INVITES_PER_PAGE = 10;

const Users = ({ lang: langProp }) => {
    const lang = langProp || getInitialLang();
    const t = useMemo(() => dict[lang] || dict.en, [lang]);

    const object = "users";
    const invitesObject = "user_invitations";
    AppContext.pages = AppContext.pages || {};
    if (!AppContext.pages[object]) AppContext.pages[object] = { pageNum: 1 };
    if (!AppContext.pages[invitesObject]) AppContext.pages[invitesObject] = { pageNum: 1 };

    const { user } = useAuth();
    const currentUserRole = useMemo(() => resolveUserRole(user), [user]);
    const isMemberRole = currentUserRole === "member";

    const [activeTab, setActiveTab] = useState("users");
    const [invitesCount, setInvitesCount] = useState(0);
    const [inviteDialogTrigger, setInviteDialogTrigger] = useState(null);

    useEffect(() => {
        if (isMemberRole && activeTab !== "users") {
            setActiveTab("users");
        }
    }, [isMemberRole, activeTab]);

    useEffect(() => {
        if (!user?.token) return;
        const handleInvitesCount = (payload = {}) => {
            const items = Array.isArray(payload.data) ? payload.data : [];
            const pendingCount = items.filter((row) => (row.status || "").toLowerCase() === "pending").length;
            setInvitesCount(pendingCount);
        };
        socket.on("user_invitations:list-data", handleInvitesCount);
        return () => {
            socket.off("user_invitations:list-data", handleInvitesCount);
        };
    }, [user?.token]);

    useEffect(() => {
        if (!user?.token) return;
        requestData(user, invitesObject, { page: 1, items_count: INVITES_PER_PAGE, status: "all" }, "list");
    }, [user?.token, invitesObject]);

    const openInviteDialogFromUsersTab = useCallback(() => {
        if (isMemberRole) return;
        setActiveTab("invites");
        setInviteDialogTrigger(Date.now());
    }, [isMemberRole]);

    const handleInviteDialogHandled = useCallback(() => {
        setInviteDialogTrigger(null);
    }, []);

    const tabItems = useMemo(() => {
        const items = [
            { key: "users", label: t.tabUsers, icon: "ti ti-users fs-4", count: null },
        ];
        if (!isMemberRole) {
            items.push({ key: "invites", label: t.tabInvites, icon: "ti ti-mail fs-4", count: invitesCount });
        }
        return items;
    }, [t, invitesCount, isMemberRole]);

    const inviteAddDisableConfig = isMemberRole ? { add: { disable: true, title: t.memberInviteRestricted } } : null;

    return (
        <>
            {tabItems.length > 1 && (
                <ul className="nav nav-pills nav-pills-custom mb-3">
                    {tabItems.map((tab) => (
                        <li className="nav-item" role="presentation" key={tab.key}>
                            <a
                                className={`nav-link d-flex align-items-center ${activeTab === tab.key ? "active" : ""}`}
                                href="javascript:void(0)"
                                onClick={() => setActiveTab(tab.key)}
                            >
                                <span><i className={tab.icon}></i></span>
                                <span className="d-none d-md-block ms-2">
                                    {tab.label}
                                    {tab.count !== null ? (
                                        <span className="badge bg-light text-dark ms-2">{tab.count}</span>
                                    ) : null}
                                </span>
                            </a>
                        </li>
                    ))}
                </ul>
            )}

            {activeTab === "users" && (
                <UsersListTab
                    t={t}
                    lang={lang}
                    user={user}
                    isMemberRole={isMemberRole}
                    openInviteDialog={openInviteDialogFromUsersTab}
                />
            )}

            {!isMemberRole && activeTab === "invites" && (
                <UserInvitationsTab
                    t={t}
                    user={user}
                    inviteAddDisableConfig={inviteAddDisableConfig}
                    inviteDialogTrigger={inviteDialogTrigger}
                    onDialogHandled={handleInviteDialogHandled}
                    invitesObject={invitesObject}
                    itemsPerPage={INVITES_PER_PAGE}
                    isMemberRole={isMemberRole}
                />
            )}
        </>
    );
};

export default Users;
